/**
 * Media Management - Moon Moon Agency
 * Handles video lazy loading with viewport-based logic
 */

/*********************************************************************************************
 * 🎥 VIDEO MODULE
 */
const VideoManager = {
	// Local configuration
	config: {
		selectors: {
			videoLazy: '.mm-video-lazy',
			videoSource: 'source'
		},
		classes: {
			videoLoaded: 'mm-video-loaded'
		},
		breakpoints: {
			mobile: 768
		},
		timing: {
			resizeDebounce: 150
		},
		unlockEvents: ['touchend', 'click']
	},

	blockedVideos: new Set(),
	_unlockHandler: null,

	/**
	 * Initialize lazy video loading
	 */
	init() {
		const videos = window.getElements(this.config.selectors.videoLazy);
		if (!videos.length) return;

		const loadVideo = (video) => {
			const source = video.querySelector(this.config.selectors.videoSource);
			if (!source?.dataset.src) return;

			// Ne recharger la ressource (source + load) que la première fois : un reload complet
			// à chaque micro-sortie/entrée du viewport cause des saccades de scroll sur iOS.
			if (!source.src) {
				source.src = source.dataset.src;
				video.preload = 'metadata';
				video.load();
			}

			video.dataset.loaded = 'true';
			video.play().catch((err) => {
				window.log('warn', 'VideoManager', 'Video auto-play failed', { error: err.message });
				if (err.name === 'NotAllowedError') this._playOnInteraction(video);
			});
		};

		const unloadVideo = (video) => {
			if (video.dataset.loaded === 'true') {
				video.pause();
				video.currentTime = 0;
				video.dataset.loaded = 'false';
			}
		};

		const handleVideoVisibility = (video) => {
			const isMobileView = window.matchMedia(`(max-width: ${this.config.breakpoints.mobile}px)`).matches;
			const isDesktop = video.classList.contains('mm-desktop');
			const isMobileVideo = video.classList.contains('mm-mobile');

			const shouldPlay = (!isDesktop && !isMobileVideo) || 
								(isDesktop && !isMobileView) || 
								(isMobileVideo && isMobileView);

			if (shouldPlay) {
				loadVideo(video);
				video.classList.add(this.config.classes.videoLoaded);
			} else {
				video.classList.remove(this.config.classes.videoLoaded);
			}
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleVideoVisibility(entry.target);
				} else {
					unloadVideo(entry.target);
				}
			});
		}, {
			rootMargin: '50px 0px',
			threshold: 0.1
		});

		videos.forEach((video) => observer.observe(video));

		// Resize handling
		const handleResize = window.debounce(() => {
			videos.forEach((video) => {
				if (video.classList.contains(this.config.classes.videoLoaded)) {
					handleVideoVisibility(video);
				}
			});
		}, this.config.timing.resizeDebounce);

		window.addEventListener('resize', handleResize);

	},

	_playOnInteraction(video) {
		this.blockedVideos.add(video);
		if (this._unlockHandler) return;

		this._unlockHandler = () => this._unlockBlockedVideos();
		this.config.unlockEvents.forEach((type) =>
			document.addEventListener(type, this._unlockHandler, { passive: true })
		);
	},

	_unlockBlockedVideos() {
		this.blockedVideos.forEach((video) => {
			if (video.dataset.loaded !== 'true') return;
			this.blockedVideos.delete(video);
			video.play().catch(() => {});
		});

		if (this.blockedVideos.size) return;
		this.config.unlockEvents.forEach((type) =>
			document.removeEventListener(type, this._unlockHandler)
		);
		this._unlockHandler = null;
	}
};

/*********************************************************************************************
 * 🎯 GLOBAL MEDIA MANAGER
 */
const GlobalMediaManager = {
	/**
	 * Initialize video management
	 */
	init() {
		VideoManager.init();
		PlayerVideoManager.init();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.VideoManager = VideoManager;
window.GlobalMediaManager = GlobalMediaManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmInitVideoLazy = () => VideoManager.init();

/*********************************************************************************************
 * ▶️ PLAYER VIDEO (click-to-play with accessible stop)
 */
const PlayerVideoManager = {
	config: {
		selectors: {
			wrapper: '[data-player-video]',
			playButton: '.mm-player-video-button',
			stopButton: '.mm-player-video-stop'
		},
		breakpoints: {
			mobile: 768
		}
	},

	init() {
		const wrappers = document.querySelectorAll(this.config.selectors.wrapper);
		if (!wrappers.length) return;

		wrappers.forEach((wrapper) => this._bind(wrapper));

		// Pause si clic en dehors de tout player en lecture
		if (!this._outsideHandlerBound) {
			this._outsideHandlerBound = true;
			document.addEventListener('click', (e) => this._handleDocumentClick(e));
		}
	},

	_bind(wrapper) {
		const playButton = wrapper.querySelector(this.config.selectors.playButton);
		const stopButton = wrapper.querySelector(this.config.selectors.stopButton);

		// Initial: stop non navigable
		if (stopButton) {
			stopButton.tabIndex = -1;
			stopButton.setAttribute('aria-hidden', 'true');
		}

		const getActiveVideo = () => {
			const isMobileView = window.matchMedia(`(max-width: ${this.config.breakpoints.mobile}px)`).matches;
			const desktopVideo = wrapper.querySelector('video.mm-desktop');
			const mobileVideo = wrapper.querySelector('video.mm-mobile');
			if (desktopVideo || mobileVideo) {
				return isMobileView ? (mobileVideo || desktopVideo || wrapper.querySelector('video')) : (desktopVideo || mobileVideo || wrapper.querySelector('video'));
			}
			return wrapper.querySelector('video');
		};

		const ensureLoaded = (video) => {
			if (!video) return Promise.resolve();
			if (video.dataset.mmLoaded === 'true') return Promise.resolve();

			const source = video.querySelector('source');
			if (source && source.dataset && source.dataset.src && !source.src) {
				source.src = source.dataset.src;
			}

			video.preload = video.preload || 'metadata';
			video.dataset.mmLoaded = 'true';

			return new Promise((resolve) => {
				if (video.readyState >= 2) {
					resolve();
					return;
				}
				const onCanPlay = () => {
					video.removeEventListener('canplay', onCanPlay);
					resolve();
				};
				video.addEventListener('canplay', onCanPlay, { once: true });
				try { video.load(); } catch (_) {}
			});
		};

		const play = async () => {
			const video = getActiveVideo();
			if (!video) return;
			await ensureLoaded(video);
			wrapper.classList.add('is-playing');
			if (stopButton) {
				stopButton.tabIndex = 0;
				stopButton.setAttribute('aria-hidden', 'false');
			}
			try { await video.play(); } catch (_) {}
		};

		const pause = () => {
			const video = getActiveVideo();
			if (!video) return;
			try { video.pause(); } catch (_) {}
			wrapper.classList.remove('is-playing');
			if (stopButton) {
				// If currently focused, blur to avoid trapping focus on a non-focusable control
				if (document.activeElement === stopButton) {
					try { stopButton.blur(); } catch (_) {}
				}
				stopButton.tabIndex = -1;
				stopButton.setAttribute('aria-hidden', 'true');
			}
		};

		// Expose controls on wrapper for global handling
		wrapper._mmPlay = play;
		wrapper._mmPause = pause;

		// Start ONLY via the play button (first interaction)
		if (playButton) {
			playButton.addEventListener('click', (e) => {
				e.preventDefault();
				play();
			});
		}

		// Click on video toggles pause only (no play from video click)
		wrapper.addEventListener('click', (e) => {
			const video = e.target && e.target.closest && e.target.closest('video');
			if (!video) return;
			if (!video.paused && !video.ended) {
				pause();
			}
		}, true);

		// Accessible stop (keyboard focusable)
		if (stopButton) {
			stopButton.addEventListener('click', (e) => {
				e.preventDefault();
				pause();
			});

			// Optional: space/enter key handling for broader support
			stopButton.addEventListener('keydown', (e) => {
				if (e.key === ' ' || e.key === 'Enter') {
					e.preventDefault();
					pause();
				}
			});
		}

		// On breakpoint change, ensure state remains consistent
		const handleResize = window.debounce ? window.debounce(() => {
			// If currently playing, keep playing new active video; otherwise stay paused
			const playing = wrapper.classList.contains('is-playing');
			if (!playing) return;
			const video = getActiveVideo();
			if (!video) return;
			ensureLoaded(video).then(() => {
				try { video.play(); } catch (_) {}
			});
		}, 200) : (() => {});

		window.addEventListener('resize', handleResize);
	},

	_handleDocumentClick(e) {
		const playingWrappers = document.querySelectorAll(`${this.config.selectors.wrapper}.is-playing`);
		if (!playingWrappers.length) return;

		playingWrappers.forEach((wrapper) => {
			// Si le clic est hors du wrapper, on met en pause
			if (!wrapper.contains(e.target)) {
				if (typeof wrapper._mmPause === 'function') {
					wrapper._mmPause();
				} else {
					// Fallback minimal si pas de méthode exposée
					const video = wrapper.querySelector('video');
					try { video && video.pause && video.pause(); } catch (_) {}
					wrapper.classList.remove('is-playing');
					const stopButton = wrapper.querySelector(this.config.selectors.stopButton);
					if (stopButton) {
						if (document.activeElement === stopButton) {
							try { stopButton.blur(); } catch (_) {}
						}
						stopButton.tabIndex = -1;
						stopButton.setAttribute('aria-hidden', 'true');
					}
				}
			}
		});
	}
};

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	GlobalMediaManager.init();
});